import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { useUnitFormatters } from '@/hooks/useUnits';
import { formatPaceDisplay, paceUnitLabel, splitUnitShort } from '@/lib/units';

interface ShareTemplateSplitsProps {
  workout: ShareableWorkout;
  /** When true, the canvas background is transparent (intended to be saved as a PNG with alpha
   *  or composited on top of a user photo). When false, a branded dark Straiv background is used. */
  transparent?: boolean;
}

const formatTimeExact = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const secRounded = Math.round(s);
  const secStr = Math.abs(s - secRounded) < 0.01 ? String(secRounded).padStart(2, '0') : s.toFixed(1);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${secStr}`;
  return `${m}:${secStr}`;
};

const formatPaceExact = (paceSeconds: number) => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const m = Math.floor(paceSeconds / 60);
  const s = Math.round(paceSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatShareTitle = (title: string) => {
  if (!title?.trim()) return title;
  const s = title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

/**
 * Splits share style. Renders a per-kilometer table of split times and paces.
 * The fastest split row is highlighted. Available in:
 *  - branded variant (dark Straiv gradient background)
 *  - transparent variant (no background, designed to overlay onto a user photo)
 */
export const ShareTemplateSplits = forwardRef<HTMLDivElement, ShareTemplateSplitsProps>(
  ({ workout, transparent = false }, ref) => {
    const { t } = useTranslation();
    const { units, formatKm, formatPace, paceSuffix } = useUnitFormatters();
    const splits = workout.splits ?? [];
    const distanceKm = workout.distance_km ?? 0;
    const paceSecondsPerKm = workout.pace_seconds_per_km;
    const durationSeconds = workout.duration_seconds;
    const splitHeader = splitUnitShort(units);

    // Identify the fastest full split for highlighting (skip partial last)
    const fullKmSplits = splits.filter(s => !s.isPartial);
    const fastestKm = fullKmSplits.length > 0
      ? fullKmSplits.reduce((best, cur) => {
          const curSec = paceTextToSeconds(cur.pace);
          const bestSec = paceTextToSeconds(best.pace);
          return curSec != null && (bestSec == null || curSec < bestSec) ? cur : best;
        }, fullKmSplits[0])
      : null;

    const labelMutedColor = transparent ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.5)';
    const rowDividerColor = transparent ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.08)';
    const rowBg = transparent ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.04)';
    const fastestRowBg = transparent ? 'rgba(10,255,194,0.18)' : 'rgba(10,255,194,0.12)';

    // Adaptive layout: pick a sizing tier based on the number of splits so a
    // long run (e.g. half-marathon, 25k, marathon) still fits on the canvas.
    //
    //   <= 14 splits  -> single column, comfortable spacing
    //   15..24 splits -> single column, tighter spacing
    //   >= 25 splits  -> two columns, compact rows (fits up to ~50)
    const numSplits = splits.length;
    const useTwoColumns = numSplits >= 25;
    let rowFont: number;
    let kmFont: number;
    let rowPadY: number;
    let headerFont: number;
    if (useTwoColumns) {
      rowFont = 28;
      kmFont = 30;
      rowPadY = 12;
      headerFont = 22;
    } else if (numSplits >= 15) {
      rowFont = 32;
      kmFont = 34;
      rowPadY = 14;
      headerFont = 22;
    } else {
      rowFont = 38;
      kmFont = 40;
      rowPadY = 20;
      headerFont = 24;
    }

    // For two-column mode, split the array in half so columns read top-to-bottom
    // independently (km 1..N/2 left, km N/2+1..N right).
    const halfIdx = Math.ceil(numSplits / 2);
    const leftCol = useTwoColumns ? splits.slice(0, halfIdx) : splits;
    const rightCol = useTwoColumns ? splits.slice(halfIdx) : [];

    const renderRow = (s: typeof splits[number], idx: number, listLen: number) => {
      const isFastest = fastestKm && s.km === fastestKm.km && !s.isPartial;
      return (
        <div
          key={`${s.km}-${idx}`}
          className="grid items-center"
          style={{
            gridTemplateColumns: '1fr 1.4fr 1.4fr',
            padding: `${rowPadY}px ${useTwoColumns ? 18 : 32}px`,
            background: isFastest ? fastestRowBg : (idx % 2 === 0 ? rowBg : 'transparent'),
            borderBottom: idx < listLen - 1 ? `1px solid ${rowDividerColor}` : 'none',
            borderRadius: isFastest ? '16px' : 0,
            marginBottom: isFastest ? '4px' : 0,
          }}
        >
          <div
            className="font-bold"
            style={{
              fontSize: `${kmFont}px`,
              color: isFastest ? '#0AFFC2' : '#ffffff',
              lineHeight: 1.2,
            }}
          >
            {s.isPartial ? `${s.km.toFixed(2)}` : `${s.km}`}
          </div>
          <div
            style={{
              fontSize: `${rowFont}px`,
              color: isFastest ? '#0AFFC2' : 'rgba(255,255,255,0.85)',
              textAlign: 'right',
              fontWeight: isFastest ? 700 : 500,
              lineHeight: 1.2,
            }}
          >
            {s.time}
          </div>
          <div
            style={{
              fontSize: `${rowFont}px`,
              color: isFastest ? '#0AFFC2' : 'rgba(255,255,255,0.85)',
              textAlign: 'right',
              fontWeight: isFastest ? 700 : 500,
              lineHeight: 1.2,
            }}
          >
            {(() => {
              const sec = paceTextToSeconds(s.pace);
              return sec != null ? formatPaceDisplay(sec, units) : s.pace;
            })()}
            {isFastest ? ' ★' : ''}
          </div>
        </div>
      );
    };

    const renderHeader = () => (
      <div
        className="grid"
        style={{
          gridTemplateColumns: '1fr 1.4fr 1.4fr',
          padding: `${useTwoColumns ? 10 : 16}px ${useTwoColumns ? 18 : 32}px`,
          color: labelMutedColor,
          fontSize: `${headerFont}px`,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        <div>{splitHeader}</div>
        <div style={{ textAlign: 'right' }}>{t('components.shareTemplates.timeHeader')}</div>
        <div style={{ textAlign: 'right' }}>{t('components.shareTemplates.paceHeader')}</div>
      </div>
    );

    return (
      <div
        ref={ref}
        className="relative overflow-hidden flex flex-col"
        style={{
          width: '1080px',
          minWidth: '1080px',
          height: '1980px',
          background: transparent
            ? 'transparent'
            : 'linear-gradient(180deg, #0a0f0d 0%, #0d1512 50%, #0a1210 100%)',
          border: transparent ? 'none' : '3px solid rgba(10, 255, 194, 0.2)',
          borderRadius: transparent ? 0 : '48px',
          padding: '80px',
          boxSizing: 'border-box',
        }}
      >
        {/* Logo */}
        <div className="flex justify-center" style={{ marginBottom: '24px' }}>
          <img src={straivLogo} alt="STRAIV" style={{ width: '360px', height: 'auto' }} />
        </div>

        {/* Title */}
        <h2 className="font-bold text-center" style={{ fontSize: '52px', color: '#0AFFC2', marginTop: '-32px' }}>
          {formatShareTitle(workout.title)}
        </h2>
        <p className="text-center" style={{ fontSize: '32px', color: labelMutedColor, marginBottom: '36px' }}>
          {units === 'imperial'
            ? t('components.shareTemplatesExtra.mileSplits', { defaultValue: 'Mile splits' })
            : t('components.shareTemplatesExtra.kilometreSplits')}
        </p>

        {/* Top summary row */}
        <div
          className="grid grid-cols-3 gap-6 text-center"
          style={{
            background: rowBg,
            border: `2px solid rgba(10, 255, 194, 0.18)`,
            borderRadius: '28px',
            padding: '28px 32px',
            marginBottom: '32px',
          }}
        >
          <div>
            <p className="uppercase tracking-wider" style={{ fontSize: '22px', color: labelMutedColor, lineHeight: 1 }}>{t('components.shareTemplates.distance')}</p>
            <p className="font-bold" style={{ fontSize: '52px', color: '#0AFFC2', lineHeight: 1.15 }}>{formatKm(distanceKm || 0)}</p>
          </div>
          <div>
            <p className="uppercase tracking-wider" style={{ fontSize: '22px', color: labelMutedColor, lineHeight: 1 }}>{t('components.shareTemplates.time')}</p>
            <p className="font-bold" style={{ fontSize: '52px', color: '#0AFFC2', lineHeight: 1.15 }}>
              {durationSeconds != null ? formatTimeExact(durationSeconds) : '--:--'}
            </p>
          </div>
          <div>
            <p className="uppercase tracking-wider" style={{ fontSize: '22px', color: labelMutedColor, lineHeight: 1 }}>{t('components.shareTemplatesExtra.avgPace')}</p>
            <p className="font-bold" style={{ fontSize: '52px', color: '#0AFFC2', lineHeight: 1.15 }}>
              {paceSecondsPerKm != null
                ? `${formatPace(paceSecondsPerKm)}${paceSuffix}`
                : (workout.average_pace || '--:--')}
            </p>
          </div>
        </div>

        {/* Splits table */}
        {splits.length === 0 ? (
          <div className="flex-1" style={{ overflow: 'hidden' }}>
            <div
              className="text-center"
              style={{ fontSize: '32px', color: labelMutedColor, padding: '60px 0' }}
            >
              {t('components.shareTemplatesExtra.noSplitsRecorded')}
            </div>
          </div>
        ) : useTwoColumns ? (
          // Two-column layout for long runs (>=25 splits, e.g. half-marathon, 25k, marathon)
          <div
            className="flex-1"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '32px',
              overflow: 'hidden',
            }}
          >
            <div>
              {renderHeader()}
              {leftCol.map((s, i) => renderRow(s, i, leftCol.length))}
            </div>
            <div>
              {renderHeader()}
              {rightCol.map((s, i) => renderRow(s, i, rightCol.length))}
            </div>
          </div>
        ) : (
          // Single-column layout for shorter runs
          <div className="flex-1" style={{ overflow: 'hidden' }}>
            {renderHeader()}
            {splits.map((s, idx) => renderRow(s, idx, splits.length))}
          </div>
        )}

        {/* Footer */}
        <p
          className="tracking-wide text-center"
          style={{ fontSize: '26px', color: labelMutedColor, marginTop: '24px' }}
        >
          {t('components.shareTemplatesExtra.poweredBy')}
        </p>
      </div>
    );
  }
);

ShareTemplateSplits.displayName = 'ShareTemplateSplits';

// "5:23" or "5:23.5" or "--:--"
function paceTextToSeconds(text: string): number | null {
  if (!text || text === '--:--') return null;
  const parts = text.split(':');
  if (parts.length !== 2) return null;
  const m = parseInt(parts[0], 10);
  const s = parseFloat(parts[1]);
  if (!isFinite(m) || !isFinite(s)) return null;
  return m * 60 + s;
}
